function getBaseUrl() {
  const configured = String(
    process.env.REACT_APP_PUSH_BACKEND_URL ||
      (typeof import.meta !== "undefined" && import.meta?.env?.VITE_PUSH_BACKEND_URL) ||
      ""
  ).trim();
  return configured;
}

function shouldRetryNetworkError(err) {
  const name = String(err?.name || "");
  const msg = String(err?.message || "").toLowerCase();
  return (
    name === "AbortError" ||
    name === "TypeError" ||
    msg.includes("network") ||
    msg.includes("failed to fetch") ||
    msg.includes("fetch")
  );
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 8000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export async function sendInstitutionNoticePush({
  title,
  body,
  level,
  recipientIds = [],
  designations = [],
  data = {},
}) {
  const baseUrl = getBaseUrl();
  if (!baseUrl) return { skipped: true, reason: "missing_backend_url" };

  const cleanDesignations = Array.isArray(designations)
    ? designations.map((x) => String(x || "").trim()).filter(Boolean)
    : [];

  const designationPayload =
    cleanDesignations.length === 1
      ? { designation: cleanDesignations[0] }
      : cleanDesignations.length > 1
        ? { designations: cleanDesignations }
        : {};

  // Regla: si se segmenta por designation(es), no enviar userIds
  const userIdsPayload =
    cleanDesignations.length > 0
      ? {}
      : { userIds: Array.isArray(recipientIds) ? recipientIds : [] };

  const dataPayload =
    data && typeof data === "object"
      ? { type: "institution_notice", level: String(level || "info"), ...data }
      : { type: "institution_notice", level: String(level || "info") };

  const resp = await fetch(`${baseUrl.replace(/\/+$/, "")}/notifications/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      title,
      body,
      data: dataPayload,
      ...userIdsPayload,
      ...designationPayload,
    }),
  });

  if (!resp.ok) {
    const msg = await resp.text();
    throw new Error(msg || "Error enviando push.");
  }
  return await resp.json();
}

export async function notifyLeaveStatusChange({
  userId,
  leaveId,
  newStatus,
  oldStatus,
  changedBy,
  comment,
} = {}) {
  const baseUrl = getBaseUrl();
  if (!baseUrl) return { skipped: true, reason: "missing_backend_url" };

  const payload = {
    userId: String(userId || "").trim(),
    leaveId: String(leaveId || "").trim(),
  };

  if (!payload.userId) throw new Error("userId requerido para notificar cambio de estado.");
  if (!payload.leaveId) throw new Error("leaveId requerido para notificar cambio de estado.");
  if (newStatus != null && String(newStatus).trim()) payload.newStatus = String(newStatus).trim();
  if (oldStatus != null && String(oldStatus).trim()) payload.oldStatus = String(oldStatus).trim();
  if (changedBy != null && String(changedBy).trim()) payload.changedBy = String(changedBy).trim();
  if (comment != null && String(comment).trim()) payload.comment = String(comment).trim();

  const endpoint = `${baseUrl.replace(/\/+$/, "")}/leaves/status-change-notify`;
  console.info("[push][leave-status] request", { endpoint, payload });

  let lastErr = null;
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      const resp = await fetchWithTimeout(
        endpoint,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        8000
      );

      const text = await resp.text();
      let json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch {
        json = null;
      }

      console.info("[push][leave-status] response", {
        attempt,
        status: resp.status,
        ok: resp.ok,
        body: json || text,
      });

      if (!resp.ok) {
        throw new Error(
          (json && (json.error || json.message)) ||
            text ||
            `Push status-change failed: HTTP ${resp.status}`
        );
      }
      return json || { ok: true };
    } catch (err) {
      lastErr = err;
      console.warn("[push][leave-status] attempt failed", {
        attempt,
        message: err?.message || String(err),
      });
      if (attempt === 1 && shouldRetryNetworkError(err)) {
        continue;
      }
      break;
    }
  }

  throw lastErr || new Error("No se pudo notificar cambio de estado.");
}
